import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Form, DatePicker } from "antd";
import { AUTH_HEADER_NAME } from "common/apis";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppSelector } from "hooks/useAppSelector";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";

const COLUMN_MAPPING =
  "mysaPartnerId:Partner Id,alteredEarnings:Altered Earnings,earningMonth:Earning Month,systemEarnings:System Earnings,alreadyPaidAmount:Already Paid Amount,remarks:Remarks,errors:Errors";

const UploadAlterEarningsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Alter Earnings Upload");
  const { authToken } = useAppSelector((state) => state.app.user);
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    processingAlterEarnings,
    uploadAlterEarningsUrl,
    partnerRoutes,
    setAlterEarningsAction,
    removeAlterEarningsAction,
  } = config;

  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [month, setMonth] = useState<string>();

  useEffect(() => {
    const { timeUploaded } = processingAlterEarnings;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) dispatch(removeAlterEarningsAction());
    }
    if (processingAlterEarnings?.key) {
      navigate(`/${partnerRoutes.SAVE_ALTER_EARNINGS}`);
    }
    return () => { message.destroy(); };
  }, [processingAlterEarnings, navigate, dispatch, removeAlterEarningsAction, partnerRoutes]);

  useEffect(() => {
    if (processingAlterEarnings?.failedRows?.length) {
      setHasErrorRows(true);
    }
  }, [processingAlterEarnings?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: any) => dispatch(setAlterEarningsAction(data));
  const onUploading = () => dispatch(removeAlterEarningsAction());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tableHeaders = useMemo(() => generateHeaders(COLUMN_MAPPING) || {}, []);

  const successRows = processingAlterEarnings?.successRows || [];
  const failedRows = processingAlterEarnings?.failedRows || [];
  const successData = successRows.map((row, i) => ({ ...row, key: `data-success-${i}` }));
  const failedData = failedRows.map((row, i) => ({ ...row, key: `data-failed-${i}` }));
  const data = [...failedData, ...successData];

  const columns: ColumnsType<any & { key: string }> = Object.keys(tableHeaders).map((key) => ({
    key,
    title: tableHeaders[key],
    dataIndex: key,
    render: (text: string, record: any) => {
      if (key === "errors") {
        return <>{record.errors?.map((e: any) => e.message).join(", ")}</>;
      }
      return <>{text}</>;
    },
  }));

  const addBorderToErrorRows = (record: any) =>
    record.errors?.length ? "error-row" : "";

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Alter Earnings Upload" />
        </Col>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <Form layout="inline">
          <Form.Item label="Select Month" name="month" required>
            <DatePicker
              picker="month"
              format="YYYY-MM"
              onChange={(val) => setMonth(val?.format("YYYY-MM"))}
            />
          </Form.Item>
        </Form>
      </Row>

      {month ? (
        <Row style={{ marginTop: "1rem" }}>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={uploadAlterEarningsUrl}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
              data={{ month }}
            />
          </Col>
        </Row>
      ) : (
        <Row style={{ marginTop: "1rem" }}>
          <Col>Select a month to upload a file.</Col>
        </Row>
      )}

      {!!Object.keys(processingAlterEarnings).length && !processingAlterEarnings.key && (
        <>
          <Row>
            <Col lg={16} xs={24}>
              <Alert
                type="error"
                message="Errors in sheet data."
                description="Please correct errors and upload again."
                showIcon
                banner
              />
            </Col>
          </Row>
          <Row>
            <Table
              size="small"
              columns={columns}
              dataSource={data}
              rowClassName={addBorderToErrorRows}
              scroll={{ y: 500, x: "100vh" }}
              className="table-with-errors"
            />
          </Row>
        </>
      )}
    </>
  );
};

export default UploadAlterEarningsPage;
